import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { authSlice } from '../slices/auth-slice';
import { loadingSlice } from '../slices/loading-slice';
import { listingsFiltersSlice } from '../slices/listings-filters-slice';
import { profileSlice } from '../slices/profile-slice';
import { neighborhoodsSlice } from '../slices/neighborhoods-slice';
import { explorerSlice } from '../slices/explorer-slice';
import { reviewerSlice } from '../slices/reviewer-slice';
import { chatSlice } from '../slices/chat-slice';
import { savedUnitsSlice } from '../slices/saved-units-slice';
/**
 * Creates a global store instance with simple slice composition
 * Core slices are added last to prevent any overrides
 *
 * @param opts - Store configuration options
 * @returns Configured store API instance with type-safe slice composition
 */
export function createGlobalStore(opts = {}) {
    const { enableDevtools = false, devtoolsName = 'GlobalStore', storage, onRehydrateStorage, slices = [], persistName = 'global-store', onCreated, } = opts;
    // Create the state creator function with simple slice composition
    const stateCreator = (set, get) => {
        // Base composed state. resetAll will be added after slice composition.
        const composedState = {};
        // Compose namespaced slices under their own keys
        // Default slices (add new core slices here)
        const defaultRegistrations = {
            loading: loadingSlice,
            auth: authSlice,
            listingsFilters: listingsFiltersSlice,
            profile: profileSlice,
            neighborhoods: neighborhoodsSlice,
            explorer: explorerSlice,
            reviewer: reviewerSlice,
            chat: chatSlice,
            savedUnits: savedUnitsSlice,
        };
        // Merge extras, allowing override by name
        for (const extra of slices) {
            defaultRegistrations[extra.name] = extra;
        }
        const registrationEntries = Object.entries(defaultRegistrations);
        if (registrationEntries.length > 0) {
            const storeApi = {
                setState: (updater) => set(updater),
                // Use the zustand get() so we always read the current store state
                getState: () => get(),
            };
            const resets = [];
            for (const [name, reg] of registrationEntries) {
                const built = reg.build(storeApi);
                composedState[name] = built;
                if (typeof built?.reset === 'function') {
                    resets.push(() => composedState[name].reset());
                }
            }
            // Expose a resetAll that invokes each slice reset
            composedState.resetAll = () => {
                for (const r of resets)
                    r();
            };
        }
        return composedState;
    };
    // Determine if persistence should be enabled based on slice-declared persist keys
    const corePersistables = [
        loadingSlice.persistKeys,
        authSlice.persistKeys,
        listingsFiltersSlice.persistKeys,
        profileSlice.persistKeys,
        neighborhoodsSlice.persistKeys,
        explorerSlice.persistKeys,
        reviewerSlice.persistKeys,
    ];
    const extrasPersistable = slices.some(s => Array.isArray(s.persistKeys) && s.persistKeys.length > 0);
    const shouldPersist = corePersistables.some(arr => Array.isArray(arr) && arr.length > 0) || extrasPersistable;
    // Create partialize function for selective persistence
    // Only persist specified keys
    const partialize = (state) => {
        const persistedState = {};
        // Build a map of namespace -> allowed keys from the registrations
        const allowed = {};
        // Default/core slices
        allowed['loading'] = loadingSlice.persistKeys;
        allowed['auth'] = authSlice.persistKeys;
        allowed['listingsFilters'] = listingsFiltersSlice.persistKeys;
        allowed['profile'] = profileSlice.persistKeys;
        allowed['neighborhoods'] = neighborhoodsSlice.persistKeys;
        allowed['explorer'] = explorerSlice.persistKeys;
        allowed['reviewer'] = reviewerSlice.persistKeys;
        allowed['savedUnits'] = savedUnitsSlice.persistKeys;
        // Extras
        for (const extra of slices) {
            allowed[extra.name] = extra.persistKeys;
        }
        for (const [key, ns] of Object.entries(state)) {
            const fields = allowed[key];
            if (Array.isArray(fields) && fields.length > 0 && ns && typeof ns === 'object') {
                persistedState[key] = Object.fromEntries(Object.entries(ns).filter(([k]) => fields.includes(k)));
            }
            // If no fields declared, do not persist this namespace at all
        }
        return persistedState;
    };
    let enhancedCreator = stateCreator;
    // Notify caller whether persistence is active
    if (typeof onCreated === 'function') {
        onCreated({ persistenceEnabled: shouldPersist });
    }
    // 1. Persistence (optional)
    if (shouldPersist) {
        const persistOptions = {
            name: persistName,
            storage: storage ? createJSONStorage(storage) : undefined,
            partialize,
        };
        if (onRehydrateStorage) {
            persistOptions.onRehydrateStorage = onRehydrateStorage;
        }
        persistOptions.merge = (persistedState, currentState) => {
            const isObject = (val) => Boolean(val) && typeof val === 'object';
            if (!isObject(persistedState))
                return currentState;
            const nextState = { ...currentState };
            for (const namespace of Object.keys(persistedState)) {
                const persistedNamespace = persistedState[namespace];
                const currentNamespace = nextState[namespace];
                if (isObject(currentNamespace) && isObject(persistedNamespace)) {
                    nextState[namespace] = {
                        ...currentNamespace,
                        ...persistedNamespace,
                    };
                }
            }
            return nextState;
        };
        enhancedCreator = persist(enhancedCreator, persistOptions);
    }
    // 2. Devtools (optional)
    if (enableDevtools) {
        enhancedCreator = devtools(enhancedCreator, { name: devtoolsName });
    }
    // 3. Immer (always)
    const wrappedCreator = immer(enhancedCreator);
    return create()(wrappedCreator);
}
